import generateH2Id from '../react/utils/GenerateH2Id';
import { ArticleNavItem } from '../types/article';

/**
 * Functionality has been pulled and adapted from
 * @react\components\ProductDisplayPage\InPageNavigation\InPageNavigation.tsx
 */
function initArticleContents(block: HTMLElement) {
  const overviewLabel: string = block.dataset.articleContentsOverviewLabel ?? 'What we delivered';
  let showOverview: boolean = false;
  if (block.dataset?.articleContentsShowOverview?.toLocaleLowerCase() === 'true') {
    showOverview = true;
  }

  parseHeadings();

  function renderList(links: ArticleNavItem[]) {
    const wrapper = block.querySelector('.article-contents__links-wrap');
    if (!wrapper) {
      console.error('Block element missing from page.');
      return;
    }

    wrapper.innerHTML = '';

    const linkElements: Node[] = [];

    if (showOverview) {
      links.unshift({
        id: `main`,
        text: overviewLabel,
        href: `#main`,
      });
    }

    links.forEach(link => {
      const element = document.createElement('li');
      element.classList.add('article-contents__link-item');
      const linkElement = document.createElement('a');
      linkElement.classList.add('article-contents__link');
      linkElement.href = link.href;

      const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svgElement.classList.add('icon', 'cta__icon');
      svgElement.setAttribute('aria-hidden', 'true');
      const useElement = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      useElement.setAttribute('href', '/dist/assets/icons/sprite.svg#arrow-down-bold');
      svgElement.appendChild(useElement);
      linkElement.appendChild(svgElement);

      linkElement.appendChild(document.createTextNode(link.text));
      linkElement.addEventListener('click', (e: MouseEvent) => {
        handleNavLinkClick(e, link);
      });

      element.appendChild(linkElement);
      linkElements.push(element);
    });

    wrapper.append(...linkElements);
  }

  function parseHeadings() {
    const items: ArticleNavItem[] = [];
    const sections = document.querySelectorAll<HTMLHeadingElement>(
      '#main .case-study-page__container h2, #main .case-study-page__bottom-content h2'
    );
    if (sections.length > 0) {
      sections.forEach((section, index) => {
        // Skip if the heading has data-ignore-h2 attribute
        if (shouldIgnoreHeading(section)) {
          return;
        }

        if (section.textContent) {
          let id = '';
          if (section.id) {
            id = section.id;
          } else {
            id = generateH2Id(section);
          }

          items.push({
            id: `pdp-nav-${index}`,
            text: section.textContent,
            href: `#${id}`,
            element: section,
          });
        }
      });
    }
    renderList(items);
  }

  function handleNavLinkClick(event: MouseEvent, item: ArticleNavItem) {
    event.preventDefault();
    const targetId = item.href.replace('#', '');
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView();
    }
  }
}

const articleContentsBlocks = document.querySelectorAll('[data-article-contents]');
articleContentsBlocks.forEach((element: HTMLElement) => {
  initArticleContents(element);
  const title = element.querySelector('.article-contents__title');
  const linksWrap = element.querySelector('.article-contents__links-wrap');
  if (title && linksWrap) {
    title.addEventListener('click', () => {
      const isClosed = title.classList.toggle('closed');
      if (isClosed) {
        linksWrap.setAttribute('hidden', 'true');
      } else {
        linksWrap.removeAttribute('hidden');
      }
    });
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const mainArticleContents = document.querySelector('.article-contents');
  const floatingArticleContents = document.querySelector('.article-contents-floating');
  if (!mainArticleContents || !floatingArticleContents) return;

  floatingArticleContents.classList.add('is-hidden');

  function toggleFloatingArticleContents() {
    const mainRect = mainArticleContents.getBoundingClientRect();
    if (mainRect.bottom < 0) {
      floatingArticleContents.classList.remove('is-hidden');
    } else {
      floatingArticleContents.classList.add('is-hidden');
    }
  }

  function updateFloatingActiveLink() {
    const links = floatingArticleContents.querySelectorAll('.article-contents__link');
    if (!links.length) return;
    const headings = Array.from(links).map(link => {
      const href = link.getAttribute('href');
      if (!href) return null;
      const id = href.replace('#', '');
      return document.getElementById(id);
    });

    let activeIndex = -1;
    const offset = 180;
    for (let i = 0; i < headings.length; i++) {
      const heading = headings[i];
      if (!heading) continue;
      const rect = heading.getBoundingClientRect();
      if (rect.top - offset <= 0) {
        activeIndex = i;
      }
    }

    links.forEach(link => link.classList.remove('active'));
    if (activeIndex >= 0) {
      links[activeIndex].classList.add('active');
    }
  }

  window.addEventListener('scroll', () => {
    toggleFloatingArticleContents();
    updateFloatingActiveLink();
  });
  toggleFloatingArticleContents();
  updateFloatingActiveLink();
});

/**
 * Function to filter out any headings that
 * should alaways be skipped in this block.
 */
function shouldIgnoreHeading(element: HTMLElement) {
  return element.hasAttribute('data-ignore-h2');
}
